import { CalendarIcon } from "@xola/icons";
import { DatePickerPopover, Input } from "@xola/ui-kit";
import { useMemoizedFn } from "ahooks";
import { DateFormat, formatDate, JsDateRange, now } from "common";
import React, { useEffect, useState } from "react";
import { DeliveryReportStatus, SelectionState } from "types";
import { useDebounce } from "use-debounce";
import { ListFilter } from "@/components/Dashboard/Filters/ListFilter";
import { ItemSummary } from "@/components/Reports/AnalyticsProductPicker";
import { useSeller } from "@/hooks/useAccount";
import {
    DELIVERY_REPORT_GUTTER_CLASS,
    DeliveryReportFilters,
    getDateRangeValue,
    getSelectionFromStatuses,
    getStatusesFromSelection,
    getStatusLabel,
    toSellerDate,
} from "@/models/DeliveryReport";

interface MessageDeliveryFiltersProps {
    filters: DeliveryReportFilters;
    onChange: (filters: DeliveryReportFilters) => void;
}

const ALL_STATUSES = Object.values(DeliveryReportStatus);

// Statuses are a fixed, static list, not a network fetch, so any fetch-time arguments a caller passes go unused.
const getStatusItems = () => {
    return { data: ALL_STATUSES.map((status) => ({ name: getStatusLabel(status), id: status })), isLoading: false };
};

export const MessageDeliveryFilters = ({ filters, onChange }: MessageDeliveryFiltersProps) => {
    const { timezoneName } = useSeller();
    const [search, setSearch] = useState(filters.search);
    const [debouncedSearch] = useDebounce(search.trim(), 500);

    const handleDebouncedSearchChange = useMemoizedFn((value: string) => {
        if (value === filters.search) {
            return;
        }

        onChange({ ...filters, search: value });
    });

    useEffect(() => {
        handleDebouncedSearchChange(debouncedSearch);
    }, [debouncedSearch, handleDebouncedSearchChange]);

    const handleStatusesChange = useMemoizedFn((selection: SelectionState) => {
        onChange({ ...filters, statuses: getStatusesFromSelection(selection) });
    });

    // The range picker is controlled and fires onChange on every day click, including the
    // intermediate { from, to: null } after the first click. It must see that value fed back or the
    // two-click selection can never complete, so track it locally and only commit a finished range.
    const [rangeValue, setRangeValue] = useState<JsDateRange | undefined>(() => getDateRangeValue(filters));

    useEffect(() => {
        setRangeValue(getDateRangeValue(filters));
    }, [filters]);

    const handleRangeChange = useMemoizedFn((value?: JsDateRange) => {
        setRangeValue(value);

        if (value?.from && value.to) {
            onChange({ ...filters, from: toSellerDate(value.from), to: toSellerDate(value.to) });
            return;
        }

        if (!value?.from && !value?.to) {
            onChange({ ...filters, from: undefined, to: undefined });
        }
    });

    const statusSelection = getSelectionFromStatuses(filters.statuses);

    return (
        <div
            className={`flex w-full items-center border-b border-gray-light py-3 align-top ${DELIVERY_REPORT_GUTTER_CLASS}`}
        >
            <div className="flex flex-wrap items-center gap-2">
                {/* BaseInput's own `w-full` wrapper absorbs a width `className`, so this outer shrink-0 div is what actually sizes it. */}
                <div className="w-64 shrink-0">
                    <Input
                        data-testid="message-delivery-search"
                        placeholder="Search by phone number"
                        value={search}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
                    />
                </div>

                <div data-testid="message-delivery-status">
                    <ListFilter
                        label="Status"
                        title="Filter Statuses"
                        target={ItemSummary}
                        value={statusSelection}
                        fetch={getStatusItems}
                        onChange={handleStatusesChange}
                    />
                </div>

                <DatePickerPopover
                    shouldShowYearPicker
                    shouldShowRelativeRanges
                    variant="range"
                    timezoneName={timezoneName}
                    value={rangeValue}
                    popoverProps={{ placement: "bottom-end", distance: 8 }}
                    onChange={handleRangeChange}
                >
                    {/* Fixed width so the trigger's right edge doesn't move when the range changes; a moving edge
                        was making Popper's flip logic jump the calendar between sides on every range change. */}
                    <div className="flex h-10 w-60 shrink-0 cursor-pointer items-center rounded border border-gray-light p-3">
                        <CalendarIcon className="mr-1" size="medium" />
                        <span className="no-translate truncate">
                            {filters.from && filters.to
                                ? `${formatDate(now(filters.from), DateFormat.DATE)} - ${formatDate(
                                      now(filters.to),
                                      DateFormat.DATE,
                                  )}`
                                : "Last updated: any"}
                        </span>
                    </div>
                </DatePickerPopover>
            </div>
        </div>
    );
};
